import { useCallback, useEffect, useRef, useState } from "react";
import { getNotice } from "@/features/notices/api";
import type { Notice } from "@/features/notices/types";
import { ApiError } from "@/lib/api";

interface UseNoticeResult {
  notice: Notice | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches a single notice by id; same race guards as use-client-notices. */
export function useNotice(noticeId: string): UseNoticeResult {
  const [notice, setNotice] = useState<Notice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    getNotice(noticeId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setNotice(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setNotice(null);
        setError(err instanceof ApiError ? err.message : "Failed to load notice.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [noticeId, reloadKey]);

  return { notice, loading, error, refetch };
}
